<div
  class="flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white"
  [class.palette-dragging]="paletteDragItem() !== null">
  <header
    class="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2.5 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:gap-3 lg:px-5 lg:py-3.5">
    <div class="flex min-w-0 items-center gap-2 lg:gap-2.5">
      <button
        type="button"
        (click)="goBackToForms()"
        aria-label="Back to forms"
        class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-800 transition hover:bg-slate-50">
        <app-svg-icon src="assets/icons/arrow-left.svg" [size]="16" />
      </button>
      @if (builderStep() !== 'config') {
        <button
          type="button"
          class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-800 transition hover:bg-slate-50 lg:hidden"
          [attr.aria-label]="mobileLeftOpen() ? 'Close build panel' : 'Open build panel'"
          [attr.aria-pressed]="mobileLeftOpen()"
          (click)="toggleMobileLeftPanel()">
          <app-svg-icon src="assets/icons/menu.svg" [size]="16" />
        </button>
      }
      <div class="hidden min-w-0 items-center gap-2.5 lg:flex">
        @if (builderStep() === 'form') {
          <h2 class="text-sm font-semibold text-slate-900">Form Preview</h2>
          <span
            class="rounded-full px-2.5 py-0.5 text-xs font-medium"
            [class.bg-slate-100]="previewStatus() === 'Empty'"
            [class.text-slate-500]="previewStatus() === 'Empty'"
            [class.bg-amber-50]="previewStatus() === 'Draft'"
            [class.text-amber-700]="previewStatus() === 'Draft'">
            {{ previewStatus() }}
          </span>
        } @else if (builderStep() === 'workflow') {
          <h2 class="text-sm font-semibold text-slate-900">Workflow</h2>
          <span class="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">
            {{ workflowActions().length }} action{{ workflowActions().length === 1 ? '' : 's' }}
          </span>
        } @else {
          <h2 class="text-sm font-semibold text-slate-900">Configuration</h2>
          <span class="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">
            Publish rules
          </span>
        }
      </div>
    </div>

    @if (numberedStepperEnabled) {
      <nav class="hidden shrink-0 justify-self-center sm:block" aria-label="Builder steps">
        <ol class="m-0 flex shrink-0 list-none items-center p-0">
          @for (step of builderSteps; track step.id; let i = $index; let last = $last) {
            <li class="flex shrink-0 items-center">
              @if (isStepCurrent(step.id)) {
                <button
                  type="button"
                  class="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-slate-200 py-1 pr-2 pl-1 text-slate-900 transition hover:text-slate-600"
                  [attr.aria-current]="'step'"
                  (click)="setBuilderStep(step.id)">
                  <span
                    class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-slate-900 bg-slate-900 text-xs font-bold leading-none text-white"
                    aria-hidden="true">{{ i + 1 }}</span>
                  <span class="hidden whitespace-nowrap text-sm font-bold leading-5 lg:inline">{{ step.label }}</span>
                </button>
              } @else if (isStepComplete(step.id)) {
                <button
                  type="button"
                  class="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-transparent py-1 pr-2 pl-1 text-slate-600 transition hover:text-slate-600"
                  (click)="setBuilderStep(step.id)">
                  <span
                    class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-slate-600 bg-slate-600 text-xs font-bold leading-none text-white"
                    aria-hidden="true">
                    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12.5 9.5 17 19 7.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span class="hidden whitespace-nowrap text-sm font-medium leading-5 lg:inline">{{ step.label }}</span>
                </button>
              } @else {
                <button
                  type="button"
                  class="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-transparent py-1 pr-2 pl-1 text-slate-400 transition hover:text-slate-600"
                  (click)="setBuilderStep(step.id)">
                  <span
                    class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-xs font-bold leading-none text-slate-400"
                    aria-hidden="true">{{ i + 1 }}</span>
                  <span class="hidden whitespace-nowrap text-sm font-medium leading-5 lg:inline">{{ step.label }}</span>
                </button>
              }
              @if (!last) {
                @if (isStepComplete(builderSteps[i + 1].id) || isStepCurrent(builderSteps[i + 1].id)) {
                  <span class="mx-1.5 h-[2px] w-4 shrink-0 bg-slate-500 lg:mx-2.5 lg:w-6" aria-hidden="true"></span>
                } @else {
                  <span class="mx-1.5 h-[2px] w-4 shrink-0 bg-slate-200 lg:mx-2.5 lg:w-6" aria-hidden="true"></span>
                }
              }
            </li>
          }
        </ol>
      </nav>
    } @else {
      <div class="hidden shrink-0 justify-self-center lg:block" aria-hidden="true"></div>
    }

    <div class="flex shrink-0 items-center justify-end gap-1.5 lg:gap-2.5">
      <button
        type="button"
        (click)="openFormSettings()"
        aria-label="Form settings"
        [disabled]="!builtForm()"
        class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-800 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white lg:px-3"
        [class.cursor-pointer]="builtForm()">
        <app-svg-icon src="assets/icons/cog-outline.svg" [size]="16" />
        <span class="hidden lg:inline">Settings</span>
      </button>
      <button
        type="button"
        (click)="openFormPreview()"
        aria-label="Open preview"
        [disabled]="!builtForm()"
        class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-800 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white lg:px-3"
        [class.cursor-pointer]="builtForm()">
        <app-svg-icon src="assets/icons/eye-outline.svg" [size]="16" />
        <span class="hidden lg:inline">Preview</span>
      </button>
      @if (builderStep() === 'form') {
        <button
          type="button"
          class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-800 lg:min-w-36 lg:px-3.5"
          (click)="workflowStepEnabled ? continueToWorkflow() : continueToConfiguration()">
          <span class="lg:hidden">Next</span>
          <span class="hidden lg:inline">{{ workflowStepEnabled ? 'Next: Workflow' : 'Next: Publish' }}</span>
          <app-svg-icon src="assets/icons/arrow-right.svg" [size]="14" />
        </button>
      } @else if (builderStep() === 'workflow') {
        <button
          type="button"
          class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-800 lg:min-w-36 lg:px-3.5"
          (click)="continueToConfiguration()">
          <span class="lg:hidden">Next</span>
          <span class="hidden lg:inline">Next: Config</span>
          <app-svg-icon src="assets/icons/arrow-right.svg" [size]="14" />
        </button>
      } @else {
        <button
          type="button"
          class="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 lg:min-w-36 lg:px-3.5"
          [disabled]="publishing()"
          (click)="publishForm()">
          <app-svg-icon src="assets/icons/send.svg" [size]="14" />
          <span class="lg:hidden">Publish</span>
          <span class="hidden lg:inline">{{ publishActionLabel() }}</span>
        </button>
      }
      <app-account-menu-button />
    </div>
  </header>

  <nav
    class="hidden shrink-0 justify-center border-b border-slate-200 bg-white lg:flex"
    aria-label="Builder sections">
    <ul class="m-0 flex list-none items-stretch gap-1 p-0">
      @for (tab of builderCompareTabs; track tab.id) {
        <li>
          <button
            type="button"
            class="-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition"
            [disabled]="tab.disabled"
            [attr.aria-current]="isCompareTabActive(tab) ? 'page' : null"
            [class.cursor-pointer]="!tab.disabled"
            [class.cursor-not-allowed]="tab.disabled"
            [class.border-primary-600]="isCompareTabActive(tab)"
            [class.text-primary-600]="isCompareTabActive(tab)"
            [class.border-transparent]="!isCompareTabActive(tab)"
            [class.text-slate-500]="!isCompareTabActive(tab) && !tab.disabled"
            [class.hover:text-slate-800]="!tab.disabled && !isCompareTabActive(tab)"
            [class.text-slate-300]="tab.disabled"
            (click)="onCompareTabClick(tab)">
            <app-svg-icon [src]="tab.iconSrc" [size]="16" />
            <span>{{ tab.label }}</span>
          </button>
        </li>
      }
    </ul>
  </nav>

  <div class="flex min-h-0 flex-1 overflow-hidden">
  <!-- Left pane (hidden on Publish / Configuration) -->
  @if (builderStep() !== 'config') {
  <aside
    class="z-30 flex max-w-full flex-col overflow-hidden border-r border-slate-200 bg-white shadow-xl lg:static lg:z-auto lg:w-[38%] lg:min-w-[340px] lg:max-w-[440px] lg:flex lg:shadow-none"
    [class.hidden]="!mobileLeftOpen()"
    [class.fixed]="mobileLeftOpen()"
    [class.inset-0]="mobileLeftOpen()">
    @if (builderStep() === 'form') {
      <header class="border-b border-slate-100 px-3 py-2.5">
        <div class="grid grid-cols-2 gap-0.5 rounded-full bg-slate-100 p-1">
          <button
            type="button"
            (click)="setBuilderMode('ai')"
            class="inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition"
            [class.bg-white]="builderMode() === 'ai'"
            [class.text-slate-800]="builderMode() === 'ai'"
            [class.shadow-sm]="builderMode() === 'ai'"
            [class.ring-1]="builderMode() === 'ai'"
            [class.ring-slate-200/80]="builderMode() === 'ai'"
            [class.text-slate-500]="builderMode() !== 'ai'">
            <img
              src="assets/icons/brand/ai.svg"
              width="16"
              height="16"
              alt=""
              aria-hidden="true"
              class="h-4 w-4" />
            AI Builder
          </button>
          <button
            type="button"
            (click)="setBuilderMode('manual')"
            class="inline-flex items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition"
            [class.bg-white]="builderMode() === 'manual'"
            [class.text-slate-800]="builderMode() === 'manual'"
            [class.shadow-sm]="builderMode() === 'manual'"
            [class.ring-1]="builderMode() === 'manual'"
            [class.ring-slate-200/80]="builderMode() === 'manual'"
            [class.text-slate-500]="builderMode() !== 'manual'">
            <app-svg-icon src="assets/icons/widgets-outline.svg" [size]="16" />
            Insert Elements
          </button>
        </div>
      </header>
    }

    @if (builderStep() === 'workflow') {
      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-6">
        <div class="flex flex-col gap-1.5">
          <h2 class="text-sm font-semibold text-slate-950">After form submit</h2>
          <p class="text-xs leading-5 text-slate-500">
            Add actions to run when someone submits this form.
          </p>
        </div>

        <div class="flex flex-col gap-1.5">
          @for (item of workflowActionCatalog; track item.id) {
            <button
              type="button"
              class="flex w-full items-start gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
              (click)="addWorkflowAction(item)">
              <span class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                <ng-container *ngTemplateOutlet="workflowActionIcon; context: { $implicit: item.id }" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-slate-900">{{ item.label }}</span>
                <span class="mt-0.5 block text-xs leading-5 text-slate-500">{{ item.description }}</span>
              </span>
            </button>
          }
        </div>
      </div>
    } @else if (builderMode() === 'ai') {
      <div #chatScroll class="relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-6">
        @if (!hasConversation()) {
          <div class="flex flex-1 flex-col items-center justify-center gap-6 text-center">
            <div class="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
              <svg class="h-7 w-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M8 10h8M8 14h5M7 4h10a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3.5L9 20v-3H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <div class="flex max-w-xs flex-col gap-2">
              <h2 class="text-lg font-semibold text-slate-950">Start with a prompt</h2>
              <p class="text-sm leading-6 text-slate-500">
                Describe the form you need, or try a suggestion below.
              </p>
            </div>

            <div class="flex w-full max-w-sm flex-col gap-2.5">
              <p class="text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                Try a suggestion
              </p>
              @for (suggestion of suggestions; track suggestion) {
                <button
                  type="button"
                  (click)="useSuggestion(suggestion)"
                  class="group flex items-center justify-between gap-3 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50">
                  <span>{{ suggestion }}</span>
                  <svg class="h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-slate-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M7 17L17 7M10 7h7v7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              }
            </div>
          </div>
        } @else {
          <div class="flex flex-col gap-4">
            @for (message of messages(); track $index) {
              <div
                class="flex"
                [class.justify-end]="message.role === 'user'"
                [class.justify-start]="message.role === 'assistant'">
                <div
                  class="max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-6"
                  [class.bg-slate-900]="message.role === 'user'"
                  [class.text-white]="message.role === 'user'"
                  [class.bg-slate-100]="message.role === 'assistant'"
                  [class.text-slate-800]="message.role === 'assistant'">
                  {{ message.content }}
                </div>
              </div>
            }

            @if (isGenerating()) {
              <div class="flex justify-start">
                <div class="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-500">
                  Building your form…
                </div>
              </div>
            }
          </div>
        }

        @if (componentsPanelOpen()) {
          <div class="absolute inset-0 z-20 flex flex-col bg-white">
            <div class="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
              <div>
                <h2 class="text-sm font-semibold text-slate-950">Available components</h2>
                <p class="text-xs text-slate-500">Tap one to add it to your prompt</p>
              </div>
              <button
                type="button"
                (click)="closeComponentsPanel()"
                class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close components">
                <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                </svg>
              </button>
            </div>

            <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-4">
              <section class="flex flex-col gap-2.5">
                <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Layout</h3>
                <div class="grid grid-cols-2 gap-2">
                  @for (item of aiLayoutComponents; track item.id) {
                    <ng-container
                      *ngTemplateOutlet="aiCatalogItem; context: { $implicit: item }" />
                  }
                </div>
              </section>

              <section class="flex flex-col gap-2.5">
                <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Basic</h3>
                <div class="grid grid-cols-2 gap-2">
                  @for (item of aiBasicComponents; track item.id) {
                    <ng-container
                      *ngTemplateOutlet="aiCatalogItem; context: { $implicit: item }" />
                  }
                </div>
              </section>

              <section class="flex flex-col gap-2.5">
                <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Intermediate</h3>
                <div class="grid grid-cols-2 gap-2">
                  @for (item of aiIntermediateComponents; track item.id) {
                    <ng-container
                      *ngTemplateOutlet="aiCatalogItem; context: { $implicit: item }" />
                  }
                </div>
              </section>

              <section class="flex flex-col gap-2.5">
                <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Advanced</h3>
                <div class="grid grid-cols-2 gap-2">
                  @for (item of aiAdvancedComponents; track item.id) {
                    <ng-container
                      *ngTemplateOutlet="aiCatalogItem; context: { $implicit: item }" />
                  }
                </div>
              </section>
            </div>
          </div>
        }
      </div>

      <div class="border-t border-slate-100 p-3">
        <div class="rounded-2xl border border-slate-200 bg-slate-50/80 px-3 pt-2 pb-2 focus-within:border-slate-300 focus-within:bg-white focus-within:ring-4 focus-within:ring-slate-900/5">
          <textarea
            #promptInput
            rows="2"
            [ngModel]="prompt()"
            (ngModelChange)="prompt.set($event)"
            (keydown)="onPromptKeydown($event)"
            placeholder="e.g. Create a contact form with name, email, phone and a message field..."
            class="w-full resize-none bg-transparent text-sm leading-6 text-slate-900 outline-none placeholder:text-slate-400"></textarea>

          <div class="mt-1.5 flex items-center justify-between gap-2">
            <div class="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                (click)="toggleComponentsPanel()"
                [attr.aria-pressed]="componentsPanelOpen()"
                class="inline-flex h-9 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                [class.border-slate-900]="componentsPanelOpen()"
                [class.bg-slate-900]="componentsPanelOpen()"
                [class.text-white]="componentsPanelOpen()">
                <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 5h7v7H4V5Zm9 0h7v7h-7V5ZM4 14h7v7H4v-7Zm9 0h7v7h-7v-7Z"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linejoin="round" />
                </svg>
                Components
                <span
                  class="rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                  [class.bg-slate-100]="!componentsPanelOpen()"
                  [class.text-slate-600]="!componentsPanelOpen()"
                  [class.bg-white/20]="componentsPanelOpen()"
                  [class.text-white]="componentsPanelOpen()">
                  {{ componentCount }}
                </span>
              </button>
            </div>

            <button
              type="button"
              (click)="generate()"
              [disabled]="!prompt().trim() || isGenerating()"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
              <span class="hidden sm:inline">Generate</span>
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 12h12M13 6l6 6-6 6"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    } @else {
      <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div class="border-b border-slate-100 px-4 py-3">
          <lib-signal-text-field
            placeholder="Search fields…"
            inputType="search"
            appearance="fill"
            prefixIcon="assets/icons/search.svg"
            [value]="paletteSearch()"
            [debounceValueChange]="false"
            (valueChange)="paletteSearch.set($event)"
            autocomplete="off" />
        </div>

        <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4 py-4">
          @if (!paletteHasMatches()) {
            <p class="px-1 py-6 text-center text-sm text-slate-500">No fields match your search.</p>
          }

          @if (layoutComponents().length > 0) {
            <section class="flex flex-col gap-2.5">
              <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Layout</h3>
              <div id="palette-layout" class="grid grid-cols-2 gap-2">
                @for (item of layoutComponents(); track item.id) {
                  <ng-container *ngTemplateOutlet="paletteCatalogItem; context: { $implicit: item }" />
                }
              </div>
            </section>
          }

          @if (basicComponents().length > 0) {
            <section class="flex flex-col gap-2.5">
              <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Basic</h3>
              <div id="palette-basic" class="grid grid-cols-2 gap-2">
                @for (item of basicComponents(); track item.id) {
                  <ng-container *ngTemplateOutlet="paletteCatalogItem; context: { $implicit: item }" />
                }
              </div>
            </section>
          }

          @if (intermediateComponents().length > 0) {
            <section class="flex flex-col gap-2.5">
              <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Intermediate</h3>
              <div id="palette-intermediate" class="grid grid-cols-2 gap-2">
                @for (item of intermediateComponents(); track item.id) {
                  <ng-container *ngTemplateOutlet="paletteCatalogItem; context: { $implicit: item }" />
                }
              </div>
            </section>
          }

          @if (advancedComponents().length > 0) {
            <section class="flex flex-col gap-2.5">
              <h3 class="px-1 text-xs font-medium uppercase tracking-wide text-slate-400">Advanced</h3>
              <div id="palette-advanced" class="grid grid-cols-2 gap-2">
                @for (item of advancedComponents(); track item.id) {
                  <ng-container *ngTemplateOutlet="paletteCatalogItem; context: { $implicit: item }" />
                }
              </div>
            </section>
          }
        </div>
      </div>

    }
  </aside>
  }

  <!-- Preview / Workflow / Config canvas -->
  <section class="flex min-h-0 min-w-0 flex-1 flex-col bg-slate-50">
    @if (builderStep() === 'config') {
      <div class="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <!-- Left: publish settings -->
        <div class="min-h-0 flex-1 overflow-y-auto border-b border-slate-200 bg-slate-50 p-5 sm:p-6 lg:w-3/5 lg:flex-none lg:border-b-0">
          <div class="mx-auto flex w-full max-w-3xl flex-col gap-4">
          <!-- Distribution -->
          <section class="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 sm:mt-0.5"
                aria-hidden="true">
                <app-svg-icon src="assets/icons/web.svg" [size]="20" />
              </span>

              <div class="min-w-0 flex-1">
                <h3 class="text-lg font-semibold text-slate-950">Distribution</h3>
                <p class="mt-1 text-xs leading-5 text-slate-500 sm:mt-0.5">
                  How this form will be published once publishing is enabled.
                </p>

                <div class="mt-5 flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-slate-800">Form URL</span>
                  <div class="flex items-center gap-2">
                    <div class="flex min-w-0 flex-1 items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                      <span
                        class="max-w-[55%] shrink-0 truncate border-r border-slate-200 bg-slate-100/80 px-3 py-2.5 text-xs text-slate-500"
                        [attr.title]="publishSlugPrefix()">
                        {{ publishSlugPrefix() }}
                      </span>
                      <span
                        class="min-w-0 flex-1 truncate px-3 py-2.5 text-sm text-slate-800"
                        [attr.title]="formPublishConfig().customSlug || formSlug()">
                        {{ formPublishConfig().customSlug || formSlug() }}
                      </span>
                    </div>
                    <lib-signal-button
                      class="shrink-0"
                      type="button"
                      appearance="outline"
                      size="sm"
                      [fullWidth]="false"
                      iconSrc="assets/icons/content-copy.svg"
                      [iconSize]="16"
                      (clicked)="copyPublishSlugPreview()">
                      {{ publishSlugCopied() ? 'Copied' : 'Copy' }}
                    </lib-signal-button>
                  </div>
                  <p class="text-xs leading-5 text-slate-500">
                    The form URL is set when the form is created and cannot be changed.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <!-- Submission reference -->
          <section class="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div class="flex items-start gap-3 sm:gap-4">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"
                aria-hidden="true">
                <app-svg-icon src="assets/icons/numeric.svg" [size]="20" />
              </span>
              <div class="min-w-0">
                <h3 class="text-lg font-semibold text-slate-950">Submission reference</h3>
                <p class="mt-1 text-xs leading-5 text-slate-500">
                  Each submission gets a unique ID (default REF-000001). Change the
                  format if you need to.
                </p>
              </div>
            </div>

            <div
              class="mt-5 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1"
              role="radiogroup"
              aria-label="Submission reference mode">
              @for (option of submissionReferenceModeOptions; track option.value) {
                <button
                  type="button"
                  role="radio"
                  class="cursor-pointer rounded-lg px-3.5 py-1.5 text-sm font-medium transition"
                  [class.bg-white]="formPublishConfig().submissionReferenceMode === option.value"
                  [class.text-slate-950]="formPublishConfig().submissionReferenceMode === option.value"
                  [class.shadow-sm]="formPublishConfig().submissionReferenceMode === option.value"
                  [class.text-slate-500]="formPublishConfig().submissionReferenceMode !== option.value"
                  [attr.aria-checked]="formPublishConfig().submissionReferenceMode === option.value"
                  (click)="onSubmissionReferenceModeChange(option.value)">
                  {{ option.label }}
                </button>
              }
            </div>

            @if (formPublishConfig().submissionReferenceMode === 'standard') {
              <div
                class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                <label class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-slate-800">Prefix (optional)</span>
                  <input
                    type="text"
                    maxlength="20"
                    [class]="previewControlClass()"
                    placeholder="REF"
                    [ngModel]="formPublishConfig().submissionReferencePrefix"
                    (ngModelChange)="updatePublishConfig('submissionReferencePrefix', $event)" />
                </label>

                <div>
                  <lib-signal-dropdown
                    label="Number of digits"
                    [items]="submissionReferenceDigitLengthOptions"
                    valuePath="value"
                    displayPath="label"
                    [value]="formPublishConfig().submissionReferenceDigitLength"
                    [debounceValueChange]="false"
                    (valueChange)="onSubmissionReferenceDigitLengthChange($event)" />
                </div>

                <div
                  class="flex min-w-[9.5rem] flex-col justify-center rounded-xl border border-sky-100 bg-sky-50 px-4 py-3">
                  <span class="text-[11px] font-medium uppercase tracking-wide text-sky-700/80">
                    Preview
                  </span>
                  <span class="mt-0.5 text-sm font-semibold text-slate-900">
                    {{ submissionReferencePreview() }}
                  </span>
                </div>
              </div>
            } @else {
              <div class="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end">
                <div class="min-w-0 flex-1">
                  <lib-signal-text-field
                    #submissionReferencePatternField
                    label="Pattern"
                    placeholder="REF- then insert tokens below"
                    [handlebars]="true"
                    [value]="formPublishConfig().submissionReferencePattern"
                    [debounceValueChange]="false"
                    (valueChange)="updatePublishConfig('submissionReferencePattern', $event)" />
                </div>

                <div
                  class="flex min-w-[9.5rem] shrink-0 flex-col justify-center rounded-xl border border-sky-100 bg-sky-50 px-4 py-3">
                  <span class="text-[11px] font-medium uppercase tracking-wide text-sky-700/80">
                    Preview
                  </span>
                  <span class="mt-0.5 text-sm font-semibold text-slate-900">
                    {{ submissionReferencePreview() }}
                  </span>
                </div>
              </div>

              <div class="mt-3 flex flex-wrap gap-1.5">
                @for (chip of submissionReferenceTokenChips; track chip.insert + chip.label) {
                  <button
                    type="button"
                    class="cursor-pointer rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                    (click)="insertSubmissionReferenceToken(chip.insert)">
                    {{ chip.label }}
                  </button>
                }
              </div>

              @if (!submissionReferencePatternHasSeq()) {
                <p class="mt-2 text-xs text-amber-700">
                  Must include exactly one sequence token (e.g. Seq 6) so each
                  submission stays unique.
                </p>
              }
            }

            <div
              class="mt-4 flex items-start gap-2 rounded-xl border border-sky-100 bg-sky-50 px-3.5 py-3 text-xs leading-5 text-sky-900/80">
              <app-svg-icon
                class="mt-0.5 shrink-0 text-sky-600"
                src="assets/icons/information-outline.svg"
                [size]="16" />
              <p>
                A unique, sequential reference will be generated for each successful
                submission of this form.
              </p>
            </div>
          </section>

          <!-- Visibility -->
          <section class="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 sm:mt-0.5"
                aria-hidden="true">
                <app-svg-icon src="assets/icons/account-multiple-outline.svg" [size]="20" />
              </span>

              <div class="min-w-0 flex-1">
                <h3 class="text-lg font-semibold text-slate-950">Visibility</h3>
                <p class="mt-1 text-xs leading-5 text-slate-500 sm:mt-0.5">
                  Choose who can open and fill out this form.
                </p>

                <div
                  class="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3"
                  role="radiogroup"
                  aria-label="Form visibility">
                  @for (option of visibilityCardOptions; track option.value) {
                    <button
                      type="button"
                      role="radio"
                      class="flex cursor-pointer flex-col gap-3 rounded-2xl border bg-white p-4 text-left transition hover:border-slate-300"
                      [class.border-primary-600]="formPublishConfig().visibility === option.value"
                      [class.border-slate-200]="formPublishConfig().visibility !== option.value"
                      [attr.aria-checked]="formPublishConfig().visibility === option.value"
                      (click)="onVisibilityChange(option.value)">
                      <div class="flex items-start justify-between gap-2">
                        <span
                          class="flex h-9 w-9 items-center justify-center rounded-xl transition"
                          [class.bg-primary-50]="formPublishConfig().visibility === option.value"
                          [class.text-primary-600]="formPublishConfig().visibility === option.value"
                          [class.bg-slate-100]="formPublishConfig().visibility !== option.value"
                          [class.text-slate-600]="formPublishConfig().visibility !== option.value"
                          aria-hidden="true">
                          <app-svg-icon [src]="option.iconSrc" [size]="18" />
                        </span>
                        <span
                          class="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border"
                          [class.border-primary-600]="formPublishConfig().visibility === option.value"
                          [class.bg-primary-600]="formPublishConfig().visibility === option.value"
                          [class.border-slate-300]="formPublishConfig().visibility !== option.value"
                          aria-hidden="true">
                          @if (formPublishConfig().visibility === option.value) {
                            <span class="h-1.5 w-1.5 rounded-full bg-white"></span>
                          }
                        </span>
                      </div>
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-slate-950">{{ option.title }}</p>
                        <p class="mt-1 text-xs leading-5 text-slate-500">{{ option.hint }}</p>
                      </div>
                    </button>
                  }
                </div>

                @if (formPublishConfig().visibility === 'workspace') {
                  <div class="mt-4 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                    <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div class="flex min-w-0 items-start gap-3">
                        <span
                          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-600"
                          aria-hidden="true">
                          <app-svg-icon src="assets/icons/account-multiple-outline.svg" [size]="20" />
                        </span>
                        <div class="min-w-0">
                          <h4 class="text-sm font-semibold text-slate-950">Select people and groups</h4>
                          <p class="mt-0.5 text-xs leading-5 text-slate-500">
                            Choose the contacts and groups who can open and submit this form.
                          </p>
                        </div>
                      </div>
                      <lib-signal-button
                        class="shrink-0"
                        type="button"
                        appearance="outline"
                        size="sm"
                        [fullWidth]="false"
                        iconSrc="assets/icons/plus.svg"
                        [iconSize]="16"
                        (clicked)="openAddPeople()">
                        Add people
                      </lib-signal-button>
                    </div>

                    <div class="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <div class="min-w-0 flex-1">
                        <lib-signal-text-field
                          inputType="email"
                          placeholder="Invite by email, comma-separated…"
                          [value]="audienceInviteDraft()"
                          [debounceValueChange]="false"
                          (valueChange)="onAudienceInviteDraftChange($event)" />
                      </div>
                      <lib-signal-button
                        class="min-w-20 shrink-0"
                        type="button"
                        size="sm"
                        [fullWidth]="false"
                        iconSrc="assets/icons/account-outline.svg"
                        [iconSize]="16"
                        [loading]="audienceInviting()"
                        [disabled]="audienceInviting()"
                        (clicked)="sendAudienceInvites()">
                        Add
                      </lib-signal-button>
                    </div>

                    @if (formPublishConfig().invitedEmails.length > 0) {
                      <div class="mt-3 flex flex-wrap gap-2">
                        @for (email of formPublishConfig().invitedEmails; track email) {
                          <span
                            class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-2.5 pr-1 text-xs font-medium text-slate-700">
                            <span class="truncate">{{ email }}</span>
                            <button
                              type="button"
                              class="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                              [attr.aria-label]="'Remove ' + email"
                              (click)="removeInvitedEmail(email)">
                              ×
                            </button>
                          </span>
                        }
                      </div>
                    }

                    <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <div class="flex items-center gap-4 border-b border-slate-200">
                        <button
                          type="button"
                          class="cursor-pointer border-b-2 px-0.5 pb-2 text-sm font-medium transition"
                          [class.border-primary-600]="audienceTab() === 'groups'"
                          [class.text-primary-700]="audienceTab() === 'groups'"
                          [class.border-transparent]="audienceTab() !== 'groups'"
                          [class.text-slate-500]="audienceTab() !== 'groups'"
                          (click)="setAudienceTab('groups')">
                          Groups ({{ filteredAudienceGroups().length }})
                        </button>
                        <button
                          type="button"
                          class="cursor-pointer border-b-2 px-0.5 pb-2 text-sm font-medium transition"
                          [class.border-primary-600]="audienceTab() === 'contacts'"
                          [class.text-primary-700]="audienceTab() === 'contacts'"
                          [class.border-transparent]="audienceTab() !== 'contacts'"
                          [class.text-slate-500]="audienceTab() !== 'contacts'"
                          (click)="setAudienceTab('contacts')">
                          Contacts ({{ filteredAudienceContacts().length }})
                        </button>
                      </div>
                      <div class="w-full sm:max-w-56">
                        <lib-signal-text-field
                          inputType="search"
                          [placeholder]="audienceTab() === 'groups' ? 'Search groups...' : 'Search contacts...'"
                          prefixIcon="assets/icons/search.svg"
                          [value]="audienceSearch()"
                          [debounceValueChange]="true"
                          (valueChange)="onAudienceSearchChange($event)" />
                      </div>
                    </div>

                    <div class="mt-3 max-h-64 overflow-y-auto rounded-xl border border-slate-200">
                      @if (audienceTab() === 'groups') {
                        @if (audienceGroupsState.loading() && audienceGroups().length === 0) {
                          <p class="px-3 py-8 text-center text-sm text-slate-500">Loading groups…</p>
                        } @else if (filteredAudienceGroups().length === 0) {
                          <p class="px-3 py-8 text-center text-sm text-slate-500">
                            {{ audienceGroups().length === 0 ? 'No groups yet. Create groups under People.' : 'No groups match your search.' }}
                          </p>
                        } @else {
                          <ul class="divide-y divide-slate-100">
                            @for (group of filteredAudienceGroups(); track group.id) {
                              <li
                                class="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition hover:bg-slate-50"
                                (click)="toggleAllowedGroup(group.id, !isGroupAllowed(group.id))">
                                <lib-signal-checkbox
                                  [value]="isGroupAllowed(group.id)"
                                  (click)="$event.stopPropagation()"
                                  (valueChange)="toggleAllowedGroup(group.id, $event)" />
                                <div
                                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                                  [ngClass]="audienceAvatarColor(group.id)"
                                  aria-hidden="true">
                                  {{ audienceInitials({ name: group.name }) }}
                                </div>
                                <div class="min-w-0 flex-1">
                                  <p class="truncate text-sm font-medium text-slate-900">{{ group.name }}</p>
                                  <p class="truncate text-xs text-slate-500">
                                    {{ group.memberCount }}
                                    {{ group.memberCount === 1 ? 'member' : 'members' }}
                                  </p>
                                </div>
                              </li>
                            }
                          </ul>
                        }
                      } @else {
                        @if (audienceContactsState.loading() && audienceContacts().length === 0) {
                          <p class="px-3 py-8 text-center text-sm text-slate-500">Loading contacts…</p>
                        } @else if (filteredAudienceContacts().length === 0) {
                          <p class="px-3 py-8 text-center text-sm text-slate-500">
                            {{ audienceContacts().length === 0 ? 'No contacts yet. Use Add people to create one.' : 'No contacts match your search.' }}
                          </p>
                        } @else {
                          <ul class="divide-y divide-slate-100">
                            @for (contact of filteredAudienceContacts(); track contact.id) {
                              <li
                                class="flex cursor-pointer items-center gap-3 px-3 py-2.5 transition hover:bg-slate-50"
                                (click)="toggleAllowedContact(contact.id, !isContactAllowed(contact.id))">
                                <lib-signal-checkbox
                                  [value]="isContactAllowed(contact.id)"
                                  (click)="$event.stopPropagation()"
                                  (valueChange)="toggleAllowedContact(contact.id, $event)" />
                                <div
                                  class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                                  [ngClass]="audienceAvatarColor(contact.id)"
                                  aria-hidden="true">
                                  {{ audienceInitials(contact) }}
                                </div>
                                <div class="min-w-0 flex-1">
                                  <p class="truncate text-sm font-medium text-slate-900">
                                    {{ contactDisplayName(contact) }}
                                  </p>
                                  <p class="truncate text-xs text-slate-500">{{ contact.email }}</p>
                                </div>
                              </li>
                            }
                          </ul>
                        }
                      }
                    </div>
                  </div>
                }

                @if (formPublishConfig().visibility === 'password_protected') {
                  <label class="mt-4 flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-slate-800">Access password</span>
                    <input
                      type="password"
                      [class]="previewControlClass()"
                      [placeholder]="
                        hasAccessPassword()
                          ? 'Leave blank to keep current password'
                          : 'Enter a password'
                      "
                      [ngModel]="formPublishConfig().accessPassword"
                      (ngModelChange)="updatePublishConfig('accessPassword', $event)" />
                    <span class="text-xs text-slate-500">
                      @if (hasAccessPassword()) {
                        A password is already set. Enter a new one only if you want to change it.
                      } @else {
                        People will need this password before they can view the form.
                      }
                    </span>
                  </label>
                }
              </div>
            </div>
          </section>

          <!-- Publishing -->
          <section class="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 sm:mt-0.5"
                aria-hidden="true">
                <app-svg-icon src="assets/icons/calendar-blank-outline.svg" [size]="20" />
              </span>

              <div class="min-w-0 flex-1">
                <h3 class="text-lg font-semibold text-slate-950">Publishing</h3>
                <p class="mt-1 text-xs leading-5 text-slate-500 sm:mt-0.5">
                  Decide when the form becomes available.
                </p>

                <div class="mt-5">
                  <lib-signal-radio-group
                    groupName="publish-timing"
                    size="sm"
                    orientation="horizontal"
                    [options]="publishTimingOptions"
                    [value]="formPublishConfig().publishTiming"
                    (valueChange)="updatePublishConfig('publishTiming', $event)" />
                </div>

                @if (formPublishConfig().publishTiming === 'scheduled') {
                  <label class="mt-4 flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-slate-800">Go-live date &amp; time</span>
                    <input
                      type="datetime-local"
                      [class]="previewControlClass()"
                      [ngModel]="formPublishConfig().scheduledAt"
                      (ngModelChange)="updatePublishConfig('scheduledAt', $event)" />
                  </label>
                }

                <div class="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-3">
                  <lib-signal-checkbox
                    size="sm"
                    label="Close form after schedule end"
                    hint="Stop accepting responses when the scheduled window ends."
                    [value]="formPublishConfig().closeOnScheduleEnd"
                    (valueChange)="updatePublishConfig('closeOnScheduleEnd', $event)" />

                  @if (formPublishConfig().closeOnScheduleEnd) {
                    <label class="mt-3 flex flex-col gap-1.5">
                      <span class="text-sm font-medium text-slate-800">Close date &amp; time</span>
                      <input
                        type="datetime-local"
                        [class]="previewControlClass()"
                        [ngModel]="formPublishConfig().scheduledEndAt"
                        (ngModelChange)="updatePublishConfig('scheduledEndAt', $event)" />
                    </label>
                  }
                </div>
              </div>
            </div>
          </section>

          <!-- Submissions -->
          <section class="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              <span
                class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 sm:mt-0.5"
                aria-hidden="true">
                <app-svg-icon src="assets/icons/format-list-checkbox.svg" [size]="20" />
              </span>

              <div class="min-w-0 flex-1">
                <h3 class="text-lg font-semibold text-slate-950">Submissions</h3>
                <p class="mt-1 text-xs leading-5 text-slate-500 sm:mt-0.5">
                  Rules for how responses are accepted and reviewed.
                </p>

                <div class="mt-5">
                  <lib-signal-checkbox
                    size="sm"
                    label="Require approval"
                    hint="Review each submission before it counts as accepted."
                    [value]="formPublishConfig().requireSubmissionApproval"
                    (valueChange)="updatePublishConfig('requireSubmissionApproval', $event)" />
                </div>

                <label class="mt-4 flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-slate-800">Max submissions</span>
                  <input
                    type="number"
                    min="1"
                    [class]="previewControlClass()"
                    placeholder="Unlimited"
                    [ngModel]="formPublishConfig().maxSubmissions"
                    (ngModelChange)="updatePublishConfig('maxSubmissions', $event)" />
                  <span class="text-xs text-slate-500">Leave empty for unlimited responses.</span>
                </label>

                <div class="mt-4">
                  <lib-signal-dropdown
                    label="Draft retention"
                    hint="How long unfinished responses are kept, or turn drafts off entirely (max 30 days)."
                    [items]="draftRetentionOptions"
                    valuePath="value"
                    displayPath="label"
                    [value]="formPublishConfig().formSubmissionDraftRetentionHours"
                    [debounceValueChange]="false"
                    (valueChange)="onDraftRetentionChange($event)" />
                </div>
              </div>
            </div>
          </section>
          </div>
        </div>

        <!-- Right: form preview (desktop only) -->
        <div class="hidden min-h-0 min-w-0 flex-1 flex-col bg-slate-50 p-4 sm:p-6 lg:flex lg:w-2/5 lg:flex-none">
          <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div class="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3 sm:px-5">
              <h3 class="text-sm font-semibold text-slate-950">Preview</h3>
              <button
                type="button"
                class="inline-flex size-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Open preview"
                [disabled]="!builtForm()"
                [class.cursor-pointer]="builtForm()"
                (click)="openFormPreview()">
                <app-svg-icon src="assets/icons/open-in-new.svg" [size]="18" />
              </button>
            </div>

            <div class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
              <div class="mx-auto w-full max-w-md">
                @if (builtForm(); as form) {
                  <div
                    class="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgb(15_23_42/0.04)]"
                    [ngStyle]="themeCssVars(form.themeColor)">
                    @if (form.coverImageUrl) {
                      <img
                        [src]="form.coverImageUrl"
                        alt=""
                        class="h-36 w-full object-cover" />
                    }
                    <div class="p-5 sm:p-6">
                      <div class="border-b border-slate-100 pb-4 text-center">
                        <h4 class="text-2xl font-medium tracking-tight text-slate-950">
                          {{ form.title }}
                        </h4>
                        @if (form.description) {
                          <p class="mt-1 text-sm leading-6 text-slate-500">
                            {{ form.description }}
                          </p>
                        }
                      </div>

                      <div class="mt-5 flex flex-col gap-4">
                        @for (field of previewFields(); track field.id) {
                          <app-form-field-preview-control
                            [field]="field"
                            [inputAppearance]="form.inputAppearance ?? 'fill'" />
                        } @empty {
                          <p class="py-6 text-center text-sm text-slate-500">
                            No fields yet. Add fields on the Form step.
                          </p>
                        }
                      </div>

                      <button
                        type="button"
                        disabled
                        class="mt-6 w-full rounded-xl py-3 text-sm font-medium text-white opacity-90"
                        [style.background-color]="themeAccentHex(form.themeColor)">
                        {{
                          orderedPages()[orderedPages().length - 1]?.cta?.primaryLabel ||
                            'Submit'
                        }}
                      </button>
                    </div>
                  </div>
                } @else {
                  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
                    <p class="text-sm font-medium text-slate-800">No form to preview</p>
                    <p class="mt-1 max-w-[14rem] text-xs leading-5 text-slate-500">
                      Build your form first, then return here to publish.
                    </p>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else if (builderStep() === 'workflow') {
      <div class="flex min-h-0 flex-1">
        <div #workflowStage class="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            #workflowViewport
            [class]="'flex min-h-0 min-w-0 flex-1 overflow-auto p-6 sm:p-8 bg-slate-50 bg-[radial-gradient(rgb(148_163_184/0.8)_1px,transparent_1px)] [background-size:calc(16px*var(--workflow-zoom,1))_calc(16px*var(--workflow-zoom,1))] [background-attachment:local] ' + (zoomAnimated() ? workflowZoomAnimatedClass : '')"
            [style.--workflow-zoom]="workflowZoom()"
            (click)="clearWorkflowSelection(); closeActionPicker()">
            <div
              #workflowSpacer
              [class]="'relative shrink-0 ' + (zoomAnimated() ? workflowZoomAnimatedClass : '')"
              [style.width.px]="workflowCanvasSize().width * workflowZoom()"
              [style.height.px]="workflowCanvasSize().height * workflowZoom()">
              <div
                [class]="'relative min-h-0 min-w-0 ' + (zoomAnimated() ? workflowZoomAnimatedClass : '')"
                [style.width.px]="workflowCanvasSize().width"
                [style.height.px]="workflowCanvasSize().height"
                [style.transform]="'scale(' + workflowZoom() + ')'"
                [style.transformOrigin]="'0 0'">
                <svg
                  class="pointer-events-none absolute inset-0 z-0 overflow-visible"
                  [attr.width]="workflowCanvasSize().width"
                  [attr.height]="workflowCanvasSize().height"
                  aria-hidden="true">
                  @for (edge of workflowEdges(); track edge.id) {
                    <path [attr.d]="edge.d" fill="none" stroke="rgb(203 213 225)" stroke-width="2" />
                  }
                </svg>

                <div
                  class="absolute left-0 top-0 z-[1] box-border flex w-80 items-center gap-3 rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-2.5 shadow-sm"
                  [style.left.px]="workflowTriggerPosition().x"
                  [style.top.px]="workflowTriggerPosition().y">
                  <span class="pointer-events-none absolute left-1/2 top-[-0.25rem] z-[2] h-2 w-2 -translate-x-1/2 rounded-full border border-slate-300 bg-white" aria-hidden="true"></span>
                  <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.625rem] bg-slate-100 text-slate-600" aria-hidden="true">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M8 7h8M8 11h8M8 15h5M6 3.5h12A1.5 1.5 0 0 1 19.5 5v14a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 19V5A1.5 1.5 0 0 1 6 3.5Z"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round" />
                    </svg>
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-semibold text-slate-950">Form submitted</p>
                    <p class="mt-0.5 text-xs text-slate-400">Trigger</p>
                  </div>
                  <span class="pointer-events-none absolute bottom-[-0.25rem] left-1/2 z-[2] h-2 w-2 -translate-x-1/2 rounded-full border border-slate-300 bg-white" aria-hidden="true"></span>
                </div>

                @for (action of workflowActions(); track action.id) {
                  <div
                    class="absolute left-0 top-0 z-[1] box-border w-80 cursor-grab rounded-2xl border border-slate-200 bg-white shadow-sm active:cursor-grabbing"
                    [class.flex]="action.type !== 'if_else'"
                    [class.items-center]="action.type !== 'if_else'"
                    [class.gap-3]="action.type !== 'if_else'"
                    [class.px-3.5]="action.type !== 'if_else'"
                    [class.py-2.5]="action.type !== 'if_else'"
                    [class.flex-col]="action.type === 'if_else'"
                    [class.items-stretch]="action.type === 'if_else'"
                    [class.gap-0]="action.type === 'if_else'"
                    [class.p-0]="action.type === 'if_else'"
                    cdkDrag
                    [cdkDragFreeDragPosition]="{ x: action.x, y: action.y }"
                    [class.border-slate-900]="selectedWorkflowActionId() === action.id"
                    [class.ring-2]="selectedWorkflowActionId() === action.id"
                    [class.ring-slate-900/10]="selectedWorkflowActionId() === action.id"
                    [class.bg-slate-50]="selectedWorkflowActionId() === action.id"
                    (cdkDragStarted)="onWorkflowNodeDragStarted()"
                    (cdkDragMoved)="onWorkflowNodeDragMoved(action, $event)"
                    (cdkDragEnded)="onWorkflowNodeDragEnded(action, $event)"
                    (click)="selectWorkflowAction(action, $event)">
                    <span class="pointer-events-none absolute left-1/2 top-[-0.25rem] z-[2] h-2 w-2 -translate-x-1/2 rounded-full border border-slate-300 bg-white" aria-hidden="true"></span>
                    @if (action.type === 'if_else') {
                      <div class="flex items-center gap-3 px-3.5 py-2.5">
                        <span
                          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.625rem] bg-amber-100 text-amber-700"
                          aria-hidden="true">
                          <ng-container *ngTemplateOutlet="workflowActionIcon; context: { $implicit: action.type }" />
                        </span>
                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-semibold text-slate-950">{{ action.label }}</p>
                          <p class="mt-0.5 text-xs text-slate-400">{{ actionKindLabel(action) }}</p>
                        </div>
                      </div>
                    } @else {
                      <span
                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[0.625rem] bg-indigo-50 text-indigo-600"
                        aria-hidden="true">
                        <ng-container *ngTemplateOutlet="workflowActionIcon; context: { $implicit: action.type }" />
                      </span>
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-semibold text-slate-950">{{ action.label }}</p>
                        <p class="mt-0.5 text-xs text-slate-400">{{ actionKindLabel(action) }}</p>
                      </div>
                    }
                    @if (action.type === 'if_else') {
                      <div class="flex flex-col gap-1.5 border-t border-slate-100 px-3 py-2 pb-3">
                        <button
                          type="button"
                          class="relative flex w-full cursor-pointer items-center justify-start gap-2 rounded-lg border-0 bg-slate-50/70 px-3 py-2 text-left text-xs leading-4 text-slate-500 transition hover:bg-slate-100"
                          (click)="onBranchRowClick(action, 'then', $event)">
                          <span class="min-w-0 flex-1 truncate text-left">
                            {{ action.config['condition']?.trim() || 'Condition' }}
                          </span>
                          <span class="pointer-events-none absolute bottom-[-0.25rem] left-1/2 z-[2] h-2 w-2 -translate-x-1/2 rounded-full border border-slate-300 bg-white" aria-hidden="true"></span>
                        </button>
                        <button
                          type="button"
                          class="relative flex w-full cursor-pointer items-center justify-start gap-2 rounded-lg border-0 bg-slate-50/70 px-3 py-2 text-left text-xs leading-4 text-slate-500 transition hover:bg-slate-100"
                          (click)="onBranchRowClick(action, 'else', $event)">
                          <span class="min-w-0 flex-1 truncate text-left">Else</span>
                          <span class="pointer-events-none absolute bottom-[-0.25rem] left-1/2 z-[2] h-2 w-2 -translate-x-1/2 rounded-full border border-slate-300 bg-white" aria-hidden="true"></span>
                        </button>
                      </div>
                    } @else {
                      <span class="pointer-events-none absolute bottom-[-0.25rem] left-1/2 z-[2] h-2 w-2 -translate-x-1/2 rounded-full border border-slate-300 bg-white" aria-hidden="true"></span>
                    }
                  </div>
                }

                @for (ifElse of ifElseActions(); track ifElse.id) {
                  <button
                    type="button"
                    class="absolute left-0 top-0 z-[2] flex h-10 w-10 cursor-grab items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 active:cursor-grabbing"
                    cdkDrag
                    [cdkDragFreeDragPosition]="branchBubblePosition(ifElse.id, 'then')"
                    aria-label="Add condition branch action"
                    title="Add to Condition branch"
                    (cdkDragStarted)="onBranchBubbleDragStarted()"
                    (cdkDragMoved)="onBranchBubbleDragMoved(ifElse.id, 'then', $event)"
                    (cdkDragEnded)="onBranchBubbleDragEnded(ifElse.id, 'then', $event)"
                    (click)="onBranchBubbleClick(ifElse.id, 'then', $event)">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="absolute left-0 top-0 z-[2] flex h-10 w-10 cursor-grab items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 active:cursor-grabbing"
                    cdkDrag
                    [cdkDragFreeDragPosition]="branchBubblePosition(ifElse.id, 'else')"
                    aria-label="Add else branch action"
                    title="Add to Else branch"
                    (cdkDragStarted)="onBranchBubbleDragStarted()"
                    (cdkDragMoved)="onBranchBubbleDragMoved(ifElse.id, 'else', $event)"
                    (cdkDragEnded)="onBranchBubbleDragEnded(ifElse.id, 'else', $event)"
                    (click)="onBranchBubbleClick(ifElse.id, 'else', $event)">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                }

                @if (showRootAddBubble()) {
                  <button
                    type="button"
                    class="absolute left-0 top-0 z-[2] flex h-10 w-10 cursor-grab items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 active:cursor-grabbing"
                    cdkDrag
                    [cdkDragFreeDragPosition]="addBubblePosition()"
                    aria-label="Add workflow action"
                    title="Add action"
                    (cdkDragStarted)="onAddBubbleDragStarted()"
                    (cdkDragMoved)="onAddBubbleDragMoved($event)"
                    (cdkDragEnded)="onAddBubbleDragEnded($event)"
                    (click)="onAddBubbleClick($event)">
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                  </button>
                }
              </div>
            </div>
          </div>

          @if (actionPickerOpen() && actionPickerScreen(); as at) {
            <div
              class="absolute z-[11] w-64 max-h-[280px] overflow-x-hidden overflow-y-auto rounded-[0.875rem] border border-slate-200 bg-white shadow-[0_10px_15px_-3px_rgb(15_23_42/0.08),0_4px_6px_-4px_rgb(15_23_42/0.06)]"
              [style.left.px]="at.x"
              [style.top.px]="at.y"
              (click)="$event.stopPropagation()">
              <p class="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Add action
              </p>
              @for (item of workflowActionCatalog; track item.id) {
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-slate-50"
                  (click)="pickWorkflowAction(item, $event)">
                  <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <ng-container *ngTemplateOutlet="workflowActionIcon; context: { $implicit: item.id }" />
                  </span>
                  <span class="min-w-0">
                    <span class="block text-sm font-medium text-slate-900">{{ item.label }}</span>
                    <span class="block text-xs text-slate-500">{{ item.description }}</span>
                  </span>
                </button>
              }
            </div>
          }

          <div class="absolute right-4 bottom-4 z-10 flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm" (click)="$event.stopPropagation()">
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Zoom out"
              title="Zoom out"
              (click)="zoomWorkflowOut()">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
            <span class="min-w-11 select-none px-1 text-center text-xs font-semibold leading-4 text-slate-600">{{ workflowZoomPercent() }}%</span>
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Zoom in"
              title="Zoom in"
              (click)="zoomWorkflowIn()">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
              aria-label="Fit to content"
              title="Fit to content"
              (click)="fitWorkflowToContent()">
              <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <div class="hidden w-[340px] shrink-0 flex-col border-l border-slate-200 bg-white lg:flex">
          @if (selectedWorkflowAction(); as action) {
            <app-workflow-action-config-panel
              [action]="action"
              (actionChange)="onWorkflowActionConfigChange($event)"
              (remove)="removeWorkflowAction(action.id)" />
          } @else {
            <div class="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
              <p class="text-sm font-medium text-slate-800">Select an action to configure</p>
              <p class="max-w-[14rem] text-xs leading-5 text-slate-500">
                Click an action on the canvas, or add one from the left catalog.
              </p>
            </div>
          }
        </div>
      </div>
    } @else {
    <div class="flex min-h-0 flex-1">
    <div [class]="'relative flex min-h-0 flex-1 flex-col ' + canvasBackgroundClass">
    <div
      class="z-[5] flex shrink-0 items-center justify-center border-b border-slate-200/70 bg-slate-50 px-6 pb-3 pt-4 sm:px-8"
      (click)="$event.stopPropagation()">
      <div class="flex w-full max-w-2xl items-center justify-center gap-3 lg:justify-between">
        <div class="hidden shrink-0 items-center gap-1.5 lg:inline-flex">
          <button
            type="button"
            (click)="setPreviewMode('desktop')"
            aria-label="Desktop preview"
            class="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg border transition"
            [class.border-primary-600]="previewMode() === 'desktop'"
            [class.bg-primary-50]="previewMode() === 'desktop'"
            [class.text-primary-600]="previewMode() === 'desktop'"
            [class.border-slate-200]="previewMode() !== 'desktop'"
            [class.bg-white]="previewMode() !== 'desktop'"
            [class.text-slate-500]="previewMode() !== 'desktop'"
            [class.hover:bg-slate-50]="previewMode() !== 'desktop'">
            <app-svg-icon src="assets/icons/laptop.svg" [size]="16" />
          </button>
          <button
            type="button"
            (click)="setPreviewMode('mobile')"
            aria-label="Mobile preview"
            class="inline-flex size-9 cursor-pointer items-center justify-center rounded-lg border transition"
            [class.border-primary-600]="previewMode() === 'mobile'"
            [class.bg-primary-50]="previewMode() === 'mobile'"
            [class.text-primary-600]="previewMode() === 'mobile'"
            [class.border-slate-200]="previewMode() !== 'mobile'"
            [class.bg-white]="previewMode() !== 'mobile'"
            [class.text-slate-500]="previewMode() !== 'mobile'"
            [class.hover:bg-slate-50]="previewMode() !== 'mobile'">
            <app-svg-icon src="assets/icons/cellphone.svg" [size]="16" />
          </button>
        </div>

        <app-form-page-pager
          class="min-w-0"
          [pages]="orderedPages()"
          [activePageId]="activePageId()"
          (activePageIdChange)="setActivePageId($event)"
          (addPage)="addFormPage()"
          (openPagesManager)="openPagesManager()" />
      </div>
    </div>

    <div
      #formPreviewScroll
      data-form-preview-scroll
      class="flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-6 pb-28 pt-4 sm:px-8 sm:pb-28"
      (click)="clearFieldSelection(); closeFormSettings()">
      <div
        class="w-full transition-all duration-300"
        [class.max-w-2xl]="previewMode() === 'desktop'"
        [class.max-w-sm]="previewMode() === 'mobile'">
        @if (builtForm(); as form) {
          <div
            class="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-[0_1px_2px_rgb(15_23_42/0.03)]"
            [ngStyle]="themeCssVars(form.themeColor)">
            @if (form.coverImageUrl) {
              <img
                [src]="form.coverImageUrl"
                alt=""
                class="h-40 w-full object-cover sm:h-48" />
            }
            <div class="p-6 sm:p-8">
            <div class="px-3">
              <div class="min-w-0 border-b border-slate-100 pb-4">
                <h3 class="text-2xl font-medium tracking-tight text-slate-950">{{ form.title }}</h3>
                @if (form.description) {
                  <p class="mt-1 text-sm leading-6 text-slate-500">{{ form.description }}</p>
                }
              </div>
            </div>

            @if (activePage()?.title || activePage()?.description) {
              <div class="mt-5 rounded-2xl bg-slate-100/90 px-5 py-4">
                @if (activePage()?.title; as pageTitle) {
                  <h4 class="text-base font-semibold tracking-tight text-slate-900">
                    {{ pageTitle }}
                  </h4>
                }
                @if (activePage()?.description; as pageDescription) {
                  <p class="text-sm leading-6 text-slate-500" [class.mt-1]="!!activePage()?.title">
                    {{ pageDescription }}
                  </p>
                }
              </div>
            }

            <div
              id="preview-fields"
              cdkDropList
              [cdkDropListData]="activePageFields()"
              (cdkDropListDropped)="onPreviewDrop($event)"
              (cdkDropListStarted)="previewDragging.set(true)"
              (cdkDropListEnded)="previewDragging.set(false)"
              class="preview-drop-list mt-5 flex min-h-[120px] flex-col gap-2"
              [class.preview-palette-drop-target]="paletteDragItem() !== null && paletteDropIndex() !== null">
              @if (
                paletteDragItem() !== null &&
                paletteDropIndex() === 0 &&
                activePageFields().length === 0
              ) {
                <div class="palette-drop-indicator" aria-hidden="true"></div>
              }
              @for (field of activePageFields(); track field.id; let fieldIndex = $index) {
                @if (paletteDragItem() !== null && paletteDropIndex() === fieldIndex) {
                  <div class="palette-drop-indicator" aria-hidden="true"></div>
                }
                <div
                  cdkDrag
                  [cdkDragData]="field"
                  [attr.data-preview-field-id]="field.id"
                  [class]="'preview-field-row relative cursor-grab rounded-xl border bg-transparent active:cursor-grabbing focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900/20 ' + previewSelectionClass(selectedFieldId() === field.id)"
                  [class.-ml-5]="selectedFieldId() === field.id"
                  [class.pl-5]="selectedFieldId() === field.id"
                  [class.!transition-none]="previewDragging()">
                  <div *cdkDragPlaceholder [class]="dragPlaceholderClass"></div>
                  <div *cdkDragPreview [class]="dragPreviewChipClass">
                    <span>{{ field.label }}</span>
                  </div>
                  @if (selectedFieldId() === field.id) {
                    <span
                      class="pointer-events-none absolute left-1 top-1/2 z-[1] -translate-y-1/2 text-slate-400"
                      aria-hidden="true">
                      <app-svg-icon src="assets/icons/drag-vertical.svg" [size]="16" />
                    </span>
                  }
                  <button
                    type="button"
                    class="flex w-full flex-col gap-1.5 rounded-xl bg-transparent px-3 py-2 text-left transition hover:bg-slate-50/60"
                    (click)="selectField(field, $event)">
                    <app-form-field-preview-control
                      [field]="field"
                      [inputAppearance]="form.inputAppearance ?? 'fill'" />
                  </button>
                </div>
              } @empty {
                <div class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-10 text-center">
                  <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-500 ring-1 ring-slate-200/80">
                    <app-svg-icon src="assets/icons/widgets-outline.svg" [size]="20" />
                  </div>
                  <p class="max-w-xs text-sm leading-6 text-slate-500">
                    Ask AI to create it, or drag and click fields manually.
                  </p>
                </div>
              }
              @if (
                paletteDragItem() !== null &&
                paletteDropIndex() === activePageFields().length &&
                activePageFields().length > 0
              ) {
                <div class="palette-drop-indicator" aria-hidden="true"></div>
              }
            </div>

            <div class="mt-8 flex items-center gap-3">
              @if ((activePage()?.order ?? 1) > 1) {
                <button
                  type="button"
                  disabled
                  class="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-800 opacity-90">
                  {{ activePage()?.cta?.backLabel || 'Back' }}
                </button>
              }
              <button
                type="button"
                disabled
                class="min-w-0 flex-1 rounded-xl py-3 text-sm font-medium text-white opacity-90"
                [style.background-color]="themeAccentHex(form.themeColor)">
                {{ activePage()?.cta?.primaryLabel || 'Submit' }}
              </button>
            </div>
            </div>
          </div>
        }
      </div>
    </div>

    <div
      class="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center px-4"
      (click)="$event.stopPropagation()">
      <div class="ai-composer-shell pointer-events-auto w-full max-w-xl">
        <div class="ai-composer-inner canvas-ai-dock flex items-center gap-2 p-2">
          <textarea
            rows="2"
            [ngModel]="canvasPrompt()"
            (ngModelChange)="canvasPrompt.set($event)"
            (keydown)="onCanvasPromptKeydown($event)"
            [disabled]="isGenerating()"
            placeholder="Describe a form or change to make…"
            class="min-h-[2.75rem] min-w-0 flex-1 resize-none bg-transparent px-2.5 py-2 text-sm leading-6 text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-60"></textarea>
          <button
            type="button"
            class="canvas-ai-dock-send"
            [disabled]="isGenerating() || !canvasPrompt().trim()"
            [attr.aria-label]="isGenerating() ? 'Sending' : 'Send'"
            (click)="submitCanvasPrompt()">
            @if (isGenerating()) {
              <span class="canvas-ai-dock-send__spinner" aria-hidden="true"></span>
            } @else {
              <svg class="h-[1.05rem] w-[1.05rem]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 18.5V6.5M12 6.5l-5 5M12 6.5l5 5"
                  stroke="currentColor"
                  stroke-width="1.85"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            }
          </button>
        </div>
      </div>
    </div>
    </div>

    <div class="relative hidden min-h-0 w-[340px] shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-white lg:flex">
      <div class="flex shrink-0 border-b border-slate-200 px-2 pt-2">
        <button
          type="button"
          class="flex-1 rounded-t-lg px-3 py-2 text-sm font-semibold transition"
          [class.bg-slate-50]="configTab() === 'page'"
          [class.text-slate-950]="configTab() === 'page'"
          [class.text-slate-500]="configTab() !== 'page'"
          (click)="setConfigTab('page')">
          Page
        </button>
        <button
          type="button"
          class="flex-1 rounded-t-lg px-3 py-2 text-sm font-semibold transition"
          [class.bg-slate-50]="configTab() === 'field'"
          [class.text-slate-950]="configTab() === 'field'"
          [class.text-slate-500]="configTab() !== 'field'"
          (click)="setConfigTab('field')">
          Form
        </button>
      </div>

      <div class="min-h-0 flex-1 overflow-hidden">
        @if (configTab() === 'page') {
          <app-form-page-step-config-panel
            [page]="activePage()"
            [canDelete]="canDeleteActivePage()"
            (pageChange)="onPageStepConfigChange($event)"
            (deletePage)="deleteActivePage()" />
        } @else if (selectedField(); as field) {
          <app-field-config-panel
            [field]="field"
            (fieldChange)="onFieldConfigChange($event)"
            (remove)="removeSelectedField()"
            (duplicate)="duplicateSelectedField()" />
        } @else {
          <div class="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
            <p class="text-sm font-medium text-slate-800">Select a field to configure</p>
            <p class="max-w-[14rem] text-xs leading-5 text-slate-500">
              Click a field on the canvas, or add one from the left catalog.
            </p>
          </div>
        }
      </div>

      @if (formSettingsOpen()) {
        <div class="absolute inset-0 z-20 flex flex-col bg-white shadow-xl">
          <div class="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
            <p class="text-sm font-semibold text-slate-950">Form settings</p>
            <button
              type="button"
              class="inline-flex size-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
              aria-label="Close form settings"
              (click)="closeFormSettings()">
              <span class="text-lg leading-none text-slate-500" aria-hidden="true">×</span>
            </button>
          </div>
          <div class="min-h-0 flex-1 overflow-hidden">
            <app-page-config-panel
              [form]="builtForm()"
              (formChange)="onPageConfigChange($event)" />
          </div>
        </div>
      }
    </div>
    </div>
    }
  </section>
  </div>
</div>

@if (paletteDragItem(); as dragItem) {
  <div
    class="palette-drag-ghost pointer-events-none fixed z-[10000]"
    [style.left.px]="paletteGhostX()"
    [style.top.px]="paletteGhostY()"
    aria-hidden="true">
    <div [class]="dragPreviewChipClass">
      <span>{{ dragItem.label }}</span>
    </div>
  </div>
}

<ng-template #paletteCatalogItem let-item>
  @if (item.available) {
    <div
      class="palette-item group flex min-w-0 cursor-grab items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2.5 text-left transition hover:border-slate-300 hover:bg-slate-50 active:cursor-grabbing"
      (pointerdown)="onPaletteDragStart(item, $event)"
      (click)="onPaletteCatalogClick(item)">
      <span class="shrink-0" [ngClass]="paletteIconToneClass(item.id)">
        <ng-container *ngTemplateOutlet="catalogIcon; context: { $implicit: item.icon }" />
      </span>
      <span class="pointer-events-none min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
        {{ item.label }}
      </span>
      <button
        type="button"
        class="pointer-events-auto invisible flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 opacity-0 transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 hover:bg-slate-100 hover:text-slate-600"
        [libTooltip]="item.description"
        tooltipPosition="right"
        (click)="$event.stopPropagation(); $event.preventDefault()"
        aria-label="Field description">
        <app-svg-icon src="assets/icons/information-outline.svg" [size]="16" />
      </button>
    </div>
  } @else {
    <div
      class="group flex min-w-0 cursor-not-allowed items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/80 px-2.5 py-2.5 text-left opacity-70"
      aria-disabled="true">
      <span class="shrink-0 text-slate-400">
        <ng-container *ngTemplateOutlet="catalogIcon; context: { $implicit: item.icon }" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-sm font-medium text-slate-500">{{ item.label }}</span>
        <span class="mt-0.5 block text-[10px] font-medium text-amber-700">Soon</span>
      </span>
      <button
        type="button"
        class="invisible flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 opacity-0 transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 hover:bg-slate-100 hover:text-slate-600"
        [libTooltip]="item.description"
        tooltipPosition="right"
        (click)="$event.stopPropagation(); $event.preventDefault()"
        aria-label="Field description">
        <app-svg-icon src="assets/icons/information-outline.svg" [size]="16" />
      </button>
    </div>
  }
</ng-template>

<ng-template #aiCatalogItem let-item>
  @if (item.available) {
    <button
      type="button"
      (click)="selectComponentForPrompt(item)"
      class="group flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2.5 text-left transition hover:border-slate-300 hover:bg-slate-50">
      <span class="shrink-0" [ngClass]="paletteIconToneClass(item.id)">
        <ng-container *ngTemplateOutlet="catalogIcon; context: { $implicit: item.icon }" />
      </span>
      <span class="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{{ item.label }}</span>
      <span
        class="invisible flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 opacity-0 transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 hover:bg-slate-100 hover:text-slate-600"
        [libTooltip]="item.description"
        tooltipPosition="right"
        (click)="$event.stopPropagation()"
        role="img"
        aria-label="Field description">
        <app-svg-icon src="assets/icons/information-outline.svg" [size]="16" />
      </span>
    </button>
  } @else {
    <div
      class="group flex min-w-0 cursor-not-allowed items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/80 px-2.5 py-2.5 text-left opacity-70"
      aria-disabled="true">
      <span class="shrink-0 text-slate-400">
        <ng-container *ngTemplateOutlet="catalogIcon; context: { $implicit: item.icon }" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-sm font-medium text-slate-500">{{ item.label }}</span>
        <span class="mt-0.5 block text-[10px] font-medium text-amber-700">Soon</span>
      </span>
      <span
        class="invisible flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 opacity-0 transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 hover:bg-slate-100 hover:text-slate-600"
        [libTooltip]="item.description"
        tooltipPosition="right"
        role="img"
        aria-label="Field description">
        <app-svg-icon src="assets/icons/information-outline.svg" [size]="16" />
      </span>
    </div>
  }
</ng-template>

<ng-template #catalogIcon let-icon>
  <app-svg-icon [src]="catalogIconSrc(icon)" [size]="20" />
</ng-template>

<ng-template #workflowActionIcon let-type>
  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    @switch (type) {
      @case ('send_email') {
        <path d="M4 7h16v10H4V7Zm0 0 8 6 8-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      }
      @case ('send_slack') {
        <path d="M8 4v3H5v3h3v3h3v-3h3V7h-3V4H8Zm7 9v3h-3v3h3v3h3v-3h3v-3h-3v-3h-3Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
      }
      @case ('call_webhook') {
        <path d="M10 8a4 4 0 0 1 6.3 2.4M14 16a4 4 0 0 1-6.3-2.4M8.5 12h7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
      }
      @case ('notify_teams') {
        <path d="M7 8h6v8H7V8Zm6 2.5 4-1.5v6l-4-1.5M5 10h1v4H5a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
      }
      @case ('if_else') {
        <path d="M7 4v5a3 3 0 0 0 3 3h7M17 9l3 3-3 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      }
      @default {
        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
      }
    }
  </svg>
</ng-template>
